/**
 * Utility module containing assorted helper functions for the NFT marketplace.
 *
 * Functions include:
 * - DOM manipulation (getMainWidth, handelTitle, getWidth, hideShowSidebar)
 * - Chain/network utilities (getDataForChain)
 * - URL/IPFS handling (stripFile, cleanUrl, ipfs2md5)
 * - Address formatting (truncateAddress)
 * - Permission checking (get_permissions)
 * - Object utilities (stripNumberedObject)
 * - Metadata caching (metaCache)
 * - Navigation helper (useNavigateBack - custom hook)
 * - Logging (logger)
 *
 * @module utils
 * @category Utilities
 */

import md5 from "md5";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import chainData from "../chains.js";
import { cacheMetadata, backendFetch } from "../lib/api";
import { from } from "./browser-buffer";

/**
 * Adjusts the sidebar position based on screen width.
 *
 * Sets the left position of the navbar to 0 if width > 1200px.
 */
function getMainWidth(): void {
    let SIDBAR = document.querySelector('.navbar-vertical.navbar-expand-xs.fixed-left')
    if (SIDBAR && getWidth() > 1200) {
        SIDBAR.style.left = '0px';
    }
}

/**
 * Sets the page title and adjusts the sidebar accordingly.
 *
 * Updates document.title and hides/shows the sidebar based on title.
 * If title is set, sidebar shows; otherwise it hides.
 *
 * @param title - The new page title
 */
const handelTitle = (title: string): void => {
    document.title = title
    let SIDBAR = document.querySelector('.navbar-vertical.navbar-expand-xs.fixed-left')
    if (SIDBAR) {
        if (document.title === title) {
            SIDBAR.style.left = '-400px';
        } else {
            SIDBAR.style.left = '0px';
        }
    }
}

// ============================================
// Auth helpers - AuthContext-aware
// ============================================

/**
 * Reads the raw auth token from sessionStorage.
 * @deprecated Prefer `useAuth().user` from `lib/auth.tsx`
 */
const getStoredToken = (): any | null => {
    try {
        const raw = sessionStorageGet('token');
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

/**
 * Checks whether the current user has the requested permission bit.
 * Uses AuthContext when available, otherwise falls back to sessionStorage.
 */
const hasPermission = (permission: string): boolean => {
    // React components should call useAuth().hasPermission() instead.
    const token = getStoredToken()
    if (!token?.account?.bmp) return false
    const perms = ['unused0', 'unused1', 'unused2', 'unused3', 'unused4', 'unused5', 'unused6', 'admin']
    const id = perms.indexOf(permission)
    if (id === -1) return false
    const bstr = (token.account.bmp >>> 0).toString(2)
    return bstr.substring(id, id + 1) === '1'
}

/**
 * Returns the current stored user id if available.
 * @deprecated Prefer `useAuth().user?.account?.id`
 */
const getCurrentUserId = (): string | number | undefined => {
    const token = getStoredToken()
    return token?.account?.id
}

/**
 * Gets the current viewport width.
 *
 * Checks documentElement first, then falls back to body.clientWidth.
 *
 * @returns The viewport width in pixels
 */
function getWidth(): number | undefined {
    if (document.documentElement && document.documentElement.clientWidth) {
        return document.documentElement.clientWidth;
    }

    if (document.body) {
        return document.body.clientWidth;
    }

    return undefined;
}

/**
 * Retrieves chain configuration data for a given chain identifier.
 *
 * Accepts chain ID as number, string, or hex string (0x...). Converts hex if needed.
 * Searches the chainData array for a matching chainId and returns the config.
 *
 * @param chain - Chain ID (decimal or hex string)
 * @returns Object with chain data and index, or null if not found
 */
export const getDataForChain = (chain: number | string): { data: typeof chainData[0]; ourId: number } | null => {
    let chainNum: number;

    if (typeof chain !== 'number') {
        if (chain?.toString()?.includes("0x")) {
            chainNum = parseInt(chain.toString(), 16);
        } else {
            chainNum = parseInt(chain.toString(), 16);
        }
    } else {
        chainNum = chain;
    }

    for (let i = 0; i < chainData.length; i++) {
        if (chainData[i]?.chainId && chainData[i]?.chainId === chainNum.toString()) {
            return { data: chainData[i], ourId: i };
        }
    }
    return null;
}

/**
 * Toggles sidebar visibility on small screens.
 *
 * On viewports < 1200px, toggles sidebar between hidden (0px) and shown (270px).
 * Also sets background color when shown.
 *
 * @private
 */
const hideShowSidebar = (): void => {
    let SIDBAR = document.querySelector('.navbar-vertical.navbar-expand-xs.fixed-left')

    if (!SIDBAR) return; // No sidebar exists, do nothing

    if (getWidth() < 1200) {
        if (SIDBAR.style.left !== "0px") {
            SIDBAR.style.left = '0px';
        } else {
            SIDBAR.style.left = '270px';
            SIDBAR.style.backgroundColor = '#fff'
        }
    }
}

/**
 * Removes the filename from a URL path.
 *
 * Given a full URL or path, strips off the last segment after the final '/'.
 *
 * @param url - The URL/path to strip filename from
 * @returns URL without the filename
 */
const stripFile = (url: string): string => {
    let f = url.split('/')[url.split('/').length - 1];
    url = url.replace(f, '');
    return url;
}

/**
 * Normalizes IPFS URLs and optionally prepends a gateway.
 *
 * Handles various IPFS URL formats:
 * - ipfs/: converts to /ipfs/<cid>
 * - ipns/: converts to /ipns/<name>
 * - ipfs://: converts to /ipfs/<cid>
 * - ipns://: converts to /ipns/<name>
 * - .ipfs. subdomain: extracts and converts
 *
 * @param url - The URL to normalize (can be null)
 * @param incGW - If true, prepends Cloudflare IPFS gateway
 * @returns Normalized URL or null if input was null
 */
const cleanUrl = (url: string | null, incGW: boolean): string | null => {
    let gateway = "https://cloudflare-ipfs.com"

    if (url !== null) {
        if (url?.includes("ipfs/")) {
            url = "/ipfs/" + url.split("ipfs/")[1]
        } else if (url?.includes("ipns/")) {
            url = "/ipns/" + url.split("ipns/")[1]
        } else if (url?.includes("ipfs://")) {
            url = "/ipfs/" + url.split("ipfs://")[1]
        } else if (url?.includes("ipns://")) {
            url = "/ipns/" + url.split("ipns://")[1]
        } else if (url?.includes(".ipfs.")) {
            let tmp = url?.split("//")[1];
            tmp = tmp?.split('.');
            let file = url?.split('/')[url?.split('/').length - 1];
            let ipfs = "";
            for (let i = 0; i < tmp.length; i++) {
                if (tmp[i].length > ipfs.length) ipfs = tmp[i];
            }
            url = "/ipfs/" + ipfs + "/" + file;
        }
    }
    if (incGW) {
        return gateway + url;
    } else {
        return url;
    }
}

/**
 * Logging utility (currently disabled).
 *
 * Originally intended to capture caller name and log to console or window.logging.
 * Currently a no-op implementation.
 *
 * @param toLog - Values to log
 */
const logger = (...toLog: any[]): void => {
    if (process.env.NODE_ENV !== 'production') {
        console.log('[LOG]', ...toLog)
    }
}

/**
 * Converts an IPFS URL to an MD5 hash and optionally a CDN URL.
 *
 * Normalizes the URL via cleanUrl, computes MD5 hash, and if addCdn is true,
 * constructs a CDN path using the first two characters of the hash.
 *
 * @param url - The IPFS URL to convert
 * @param addCdn - If true, returns full CDN URL; otherwise returns just MD5 hash
 * @returns MD5 hash or CDN URL, or null if input is null
 */
const ipfs2md5 = (url: string | null, addCdn: boolean): string | null => {
    if (url !== null) {
        let cdn = "https://cdn.stealthisnft.xyz/c/"
        url = cleanUrl(url, false) || url;
        let mdurl = url ? md5(url) : "00qmbgz";
        let f2md5 = mdurl.substr(0, 2);
        cdn += f2md5 + "/";

        if (addCdn) {
            return cdn + mdurl;
        } else {
            return mdurl;
        }
    } else {
        return null;
    }
}

/**
 * Truncates a blockchain address for display.
 *
 * Shows first 6 characters and last 4 characters, separated by "....".
 * Returns null if address is undefined or null.
 *
 * @param address - The full blockchain address
 * @returns Truncated address like "0x1234....abcd" or null
 */
function truncateAddress(address: string | undefined | null): string | null {
    if (address !== undefined && address !== null) {
        let retStr = address.substr(0, 6) + "...." + address.substr(-4);
        return retStr;
    } else {
        return null;
    }
}

/**
 * Fetches and caches NFT metadata from a URL or backend cache.
 *
 * 1. Checks backend cache using URL hash (ipfs2md5) for a hit.
 * 2. If not cached, fetches JSON from URL directly (for HTTP) or tries multiple IPFS gateways.
 * 3. Stores fetched metadata in backend cache for future requests.
 *
 * Returns cached data with view/like/comment counters, or fresh metadata.
 *
 * @param url - The metadata URL (HTTP or IPFS)
 * @param collectionId - The collection ID this NFT belongs to
 * @param currentOwner - Owner address for potential access control
 * @returns Metadata object with added __views, __likes, __comms
 */
const metaCache = async (url: string, collectionId: string, currentOwner?: string): Promise<any> => {
    let hash: string, clean: string;

    if (!url?.includes('ipfs') && !url?.includes('ipns')) {
        hash = ipfs2md5(url, false) || "";
        clean = url;
    } else {
        hash = ipfs2md5(cleanUrl(url, false), false) || "";
        clean = cleanUrl(url, false) || url;
    }

    let data = await cacheMetadata({ hash, collectionId, clean, currentOwner });

    if (!data?.metadata) {
        let theJson: any = {};

        if (!url?.includes('ipfs') && !url?.includes('ipns')) {
            await fetch(url)
                .then(async function (value) {
                    theJson = await value.json();
                })
                .catch(async e => {
                    try {
                        theJson = await backendFetch(url);
                    } catch (err) {
                        toast.error("Unfortunately we can't find the JSON data for this item");
                    }
                });
        } else {
            await Promise.any([
                fetch("https://nftstorage.link" + cleanUrl(url, false)!),
                fetch("https://gateway.pinata.cloud" + cleanUrl(url, false)!),
                fetch("https://ipfs.eth.aragon.network" + cleanUrl(url, false)!),
                fetch("https://cloudflare-ipfs.com" + cleanUrl(url, false)!),
                fetch(cleanUrl(url, true)!)
            ]).then(async function (value) {
                theJson = await value.json();
            }).catch(async e => {
                theJson = await backendFetch(url);
            });
        }

        await cacheMetadata({ hash, collectionId, clean, theJson });
        theJson.__views = "0";
        theJson.__likes = "0";
        theJson.__comms = "0";

        return theJson;
    } else {
        if (data.hasOwnProperty("metadata")) {
            let doob = data.metadata;
            doob.__views = data.views;
            doob.__likes = data.likes;
            doob.__comms = data.comms;
            logger("DOOB", data, data.meta, doob);
            return doob;
        } else {
            logger("ESS WUN", data);
            return data;
        }
    }
}

/**
 * Custom hook to handle browser back button navigation.
 *
 * Prevents default popstate behavior and allows custom callback handling.
 * The callback is ignored on initial mount, but can be used on subsequent navigations.
 *
 * @param callback - Function to call when back button is pressed (after initial mount)
 * @private
 */
const useNavigateBack = (callback: () => void): void => {
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;

            window.addEventListener('popstate', function (event) {
                event.stopImmediatePropagation();
            }, false);
        } else {
            // In my special case this fix was needed:
            // window.history.pushState(null, '', document.URL);
        }
    }, [callback]);
}

/**
 * Creates a new object excluding numeric keys.
 *
 * Iterates over an object's entries and includes only those with non-numeric keys.
 * All values are converted to strings via .toString().
 *
 * @param _object - The source object
 * @returns New object with non-numeric keys and string values
 */
function stripNumberedObject(_object: Record<string, any>): Record<string, string> {
    let out: Record<string, string> = {}
    for (const [k, v] of Object.entries(_object)) {
        if (isNaN(parseInt(k))) {
            out[k] = v.toString();
        }
    }
    return out;
}

/**
 * Checks if the current user has a specific permission.
 *
 * Prefer `useAuth().hasPermission()` from `lib/auth.tsx`.
 * This helper keeps backward compatibility for non-React callers.
 *
 * @param which - Permission name to check (e.g., 'admin')
 * @returns True if user has the permission, false otherwise
 */
function get_permissions(which: string): boolean {
    return hasPermission(which)
}

export {
    getMainWidth,
    handelTitle,
    getWidth,
    hideShowSidebar,
    ipfs2md5,
    metaCache,
    cleanUrl,
    truncateAddress,
    logger,
    useNavigateBack,
    stripFile,
    get_permissions,
    hasPermission,
    getCurrentUserId,
    getStoredToken,
    stripNumberedObject
};
